/* RPG layer: rank, skills, achievements and notifications. All of it is derived from spaced review
 * progress (see lib/learning.js), so it rewards remembering, not grinding. */
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ACHIEVEMENTS, MAX_BOX } from "../lib/learning";
import { progress } from "../lib/progress";
import { hrefFor } from "../lib/router";
import { dismissToast, formatWhen, showToast, useLearning, useToasts } from "../lib/useLearning";
import { ProgressBar } from "./Badges";
import { AnvilIcon, ChevronRightIcon, TrophyIcon, XIcon } from "./Icons";

export function RankChip({ track }) {
  const { t } = useTranslation();
  const { rank, points, due } = useLearning(track);
  return (
    <a className="rank-chip" href={hrefFor(track.id)} title={t("forge.points", { points })}>
      <AnvilIcon size={15} />
      <span className="rank-chip__name">{t(`ranks.${rank.id}`)}</span>
      {due.length > 0 && <span className="rank-chip__due">{due.length}</span>}
    </a>
  );
}

export function ForgePanel({ track }) {
  const { t, i18n } = useTranslation();
  const { rank, points, due, nextDue, unlocked, now } = useLearning(track);
  const floor = rank.points;
  const ceiling = rank.next ? rank.next.points : floor;

  return (
    <aside className="forge-panel" aria-labelledby="forge-title">
      <p className="eyebrow" id="forge-title">
        {t("forge.title")}
      </p>
      <div className="forge-panel__rank">
        <span className="forge-panel__badge" aria-hidden="true">
          <AnvilIcon size={22} />
        </span>
        <div>
          <strong>{t(`ranks.${rank.id}`)}</strong>
          <span className="muted small">{t("forge.points", { points })}</span>
        </div>
      </div>
      <ProgressBar value={points - floor} total={Math.max(ceiling - floor, 1)} label={t("forge.points", { points })} />
      <p className="muted small forge-panel__next">
        {rank.next
          ? t("forge.nextRank", { points: rank.next.points - points, rank: t(`ranks.${rank.next.id}`) })
          : t("forge.maxRank")}
      </p>

      <div className="forge-panel__review">
        {due.length > 0 ? (
          <>
            <strong>{t("forge.due", { count: due.length })}</strong>
            <a className="button button--primary" href={hrefFor(track.id, "review")}>
              {t("forge.startReview")}
              <ChevronRightIcon />
            </a>
          </>
        ) : (
          <>
            <strong>{t("forge.nothingDue")}</strong>
            <span className="muted small">
              {unlocked === 0
                ? t("forge.noCards")
                : t("forge.nextReview", { when: formatWhen(nextDue, now, i18n.resolvedLanguage) })}
            </span>
          </>
        )}
      </div>
      <p className="muted small forge-panel__explain">{t("forge.explain")}</p>
    </aside>
  );
}

export function SkillsPanel({ track, concepts }) {
  const { t } = useTranslation();
  const { mastery } = useLearning(track);

  return (
    <section className="side-panel">
      <h2>{t("skills.title")}</h2>
      <p className="muted small">{t("skills.hint")}</p>
      <ul className="skills">
        {concepts.map((concept) => {
          const skill = mastery[concept];
          const label = skill ? t("skills.level", { concept, level: skill.level }) : t("skills.locked", { concept });
          return (
            <li key={concept} className={`skill ${skill ? "is-learned" : ""}`} title={label}>
              <span className="skill__name">{concept}</span>
              <span className="skill__pips" aria-label={label}>
                {Array.from({ length: MAX_BOX }, (_, i) => (
                  <span key={i} className={skill && i < skill.level ? "is-filled" : ""} />
                ))}
              </span>
              {skill?.due > 0 && <span className="skill__due" title={t("skills.due", { count: skill.due })} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function AchievementsPanel({ track }) {
  const { t } = useTranslation();
  const { state } = useLearning(track);
  const earned = ACHIEVEMENTS.filter((id) => state.achievements[id]);

  return (
    <section className="side-panel">
      <h2>
        {t("achievements.title")}{" "}
        <span className="muted small">{t("achievements.count", { earned: earned.length, total: ACHIEVEMENTS.length })}</span>
      </h2>
      <ul className="achievements">
        {ACHIEVEMENTS.map((id) => {
          const unlocked = Boolean(state.achievements[id]);
          return (
            <li key={id} className={`achievement ${unlocked ? "is-unlocked" : ""}`}>
              <span className="achievement__icon" aria-hidden="true">
                <TrophyIcon size={16} />
              </span>
              <span>
                <strong>{t(`achievements.items.${id}.name`)}</strong>
                <span className="muted small">{t(`achievements.items.${id}.description`)}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Records newly earned achievements and announces them, together with rank-ups. */
export function ProgressWatcher({ track }) {
  const { t } = useTranslation();
  const { achievements, rank } = useLearning(track);
  const lastRank = useRef(null);

  useEffect(() => {
    for (const id of progress.recordAchievements(achievements)) {
      showToast({ title: t("achievements.unlocked"), body: t(`achievements.items.${id}.name`) });
    }
  }, [achievements, t]);

  useEffect(() => {
    if (lastRank.current !== null && rank.index > lastRank.current) {
      showToast({ title: t("forge.rankUp", { rank: t(`ranks.${rank.id}`) }), body: t("forge.rankUpBody"), kind: "rank" });
    }
    lastRank.current = rank.index;
  }, [rank, t]);

  return null;
}

export function Toasts() {
  const items = useToasts();
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.kind}`}>
          <span className="toast__icon" aria-hidden="true">
            {toast.kind === "rank" ? <AnvilIcon size={18} /> : <TrophyIcon size={18} />}
          </span>
          <div className="toast__text">
            <strong>{toast.title}</strong>
            <span>{toast.body}</span>
          </div>
          <button type="button" className="icon-button" onClick={() => dismissToast(toast.id)} aria-label="Dismiss">
            <XIcon size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

